import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import { surveyService } from '../../services/survey.service';
import LoadingSpinner from '../ui/LoadingSpinner';
import type { SurveyChartQuestion, SurveyChartSlice } from '../../types/survey';

ChartJS.register(ArcElement, Tooltip, Legend);

const PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#14b8a6', '#f97316', '#22c55e', '#fb923c',
  '#06b6d4', '#84cc16', '#64748b', '#ec4899',
];
const NOT_RESPONDED_COLOR = '#e5e7eb';

interface ResponsesChartProps {
  questionnaireId: number;
  filters?: {
    date_from?: string;
    date_to?: string;
    option_id?: number;
    user_account_id?: number;
    assistant_id?: number;
    whatsapp_id?: number;
    contact_id?: number;
  };
}

interface PieChartProps {
  labels: string[];
  values: number[];
  colors: string[];
  title: string;
}

const PieChart: React.FC<PieChartProps> = ({ labels, values, colors, title }) => {
  const total = values.reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 text-center">{title}</p>
      <div className="w-36 h-36">
        <Pie
          data={{
            labels,
            datasets: [
              {
                data: values,
                backgroundColor: colors,
                borderWidth: 2,
                borderColor: '#ffffff',
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0';
                    return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
                  },
                },
              },
            },
          }}
        />
      </div>
      <div className="flex flex-col gap-1 mt-3 w-full">
        {labels.map((label, i) => {
          const pct = total > 0 ? ((values[i] / total) * 100).toFixed(1) : '0';
          return (
            <div key={label} className="flex items-center gap-1.5 text-xs">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-gray-200"
                style={{ backgroundColor: colors[i] }}
              />
              <span className="truncate flex-1 text-gray-700 dark:text-gray-300">{label}</span>
              <span className="text-gray-400 flex-shrink-0">{values[i]} ({pct}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface QuestionDualChartProps {
  question: SurveyChartQuestion;
}

const QuestionDualChart: React.FC<QuestionDualChartProps> = ({ question }) => {
  const { slices, not_responded_count, question_text, question_type } = question;

  if (question_type === 'text') {
    return (
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">{question_text}</p>
        <div className="flex flex-col gap-1">
          {slices.map((s: SurveyChartSlice) => (
            <div key={s.label} className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded px-2 py-1">
              "{s.label}"
              <span className="ml-2 text-xs text-gray-400">×{s.count}</span>
            </div>
          ))}
          {not_responded_count > 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Não respondidos: <span className="font-medium">{not_responded_count}</span>
            </div>
          )}
          {slices.length === 0 && not_responded_count === 0 && (
            <p className="text-xs text-gray-400">Sem dados</p>
          )}
        </div>
      </div>
    );
  }

  if (slices.length === 0 && not_responded_count === 0) {
    return (
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{question_text}</p>
        <p className="text-xs text-gray-400 text-center py-2">Sem dados</p>
      </div>
    );
  }

  const sliceLabels = slices.map((s) => s.label);
  const sliceValues = slices.map((s) => s.count);
  const sliceColors = PALETTE.slice(0, slices.length);

  // Chart with not_responded (includes "Não respondidos" slice)
  const withLabels = [...sliceLabels, ...(not_responded_count > 0 ? ['Não respondidos'] : [])];
  const withValues = [...sliceValues, ...(not_responded_count > 0 ? [not_responded_count] : [])];
  const withColors = [...sliceColors, ...(not_responded_count > 0 ? [NOT_RESPONDED_COLOR] : [])];

  return (
    <div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-4">{question_text}</p>
      <div className="flex gap-6 items-start">
        {slices.length > 0 && (
          <PieChart
            title="Apenas respondidos"
            labels={sliceLabels}
            values={sliceValues}
            colors={sliceColors}
          />
        )}
        {(slices.length > 0 || not_responded_count > 0) && not_responded_count > 0 && (
          <>
            <div className="w-px bg-gray-200 dark:bg-gray-700 self-stretch" />
            <PieChart
              title="Incluindo não respondidos"
              labels={withLabels}
              values={withValues}
              colors={withColors}
            />
          </>
        )}
        {slices.length === 0 && not_responded_count > 0 && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Não respondidos: <span className="font-medium">{not_responded_count}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const ResponsesChart: React.FC<ResponsesChartProps> = ({ questionnaireId, filters }) => {
  const { data: chartQuestions = [], isLoading } = useQuery({
    queryKey: ['survey-responses-chart', questionnaireId, filters],
    queryFn: () => surveyService.listResponsesForChart(questionnaireId, filters),
    staleTime: 30_000,
  });

  const hasAnyData = chartQuestions.some(
    (q) => q.slices.length > 0 || q.not_responded_count > 0
  );

  if (isLoading) {
    return (
      <div className="mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!hasAnyData) return null;

  return (
    <div className="mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
        Distribuição de Respostas
      </h3>
      <div className="flex flex-col gap-6 divide-y divide-gray-100 dark:divide-gray-700">
        {chartQuestions.map((q) => (
          <div key={q.question_id} className="pt-4 first:pt-0">
            <QuestionDualChart question={q} />
          </div>
        ))}
      </div>
    </div>
  );
};
